import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, ChevronRight, Eye, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useTeachingAssignmentForm } from '@generated/hooks/useTeachingAssignmentForm';
import { TeachingHero } from '@generated/components/teaching/TeachingHero';
import { TeachingSectionNav } from '@generated/components/teaching/TeachingSectionNav';
import { PersonalInfoSection } from '@generated/components/teaching/PersonalInfoSection';
import { EmploymentHistorySection } from '@generated/components/teaching/EmploymentHistorySection';
import { CourseAssignmentSection } from '@generated/components/teaching/CourseAssignmentSection';
import { AdditionalPositionSection } from '@generated/components/teaching/AdditionalPositionSection';
import { NotesAndSecuritySection } from '@generated/components/teaching/NotesAndSecuritySection';
import { TeachingReviewDocument } from '@generated/components/teaching/TeachingReviewDocument';
import { TeachingReceipt } from '@generated/components/teaching/TeachingReceipt';
import {
  getTeachingAssignmentMeta,
  submitTeachingAssignment,
  uploadTeachingApprovalFile,
  type CourseLookupItem,
} from '@generated/server/teachingAssignment';
import { errorCount } from '@generated/lib/teachingAssignment/validate';

export const Route = createFileRoute('/_app/forms/teaching-assignment')({
  component: TeachingAssignmentPage,
});

type FormMode = 'edit' | 'review' | 'submitted';

function TeachingAssignmentPage() {
  const api = useTeachingAssignmentForm();
  const [mode, setMode] = useState<FormMode>('edit');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [referenceCode, setReferenceCode] = useState<string>('');
  const [createdItemId, setCreatedItemId] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [availableCourses, setAvailableCourses] = useState<CourseLookupItem[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Load available courses and meta
  useEffect(() => {
    let active = true;
    getTeachingAssignmentMeta()
      .then((res) => {
        if (!active) return;
        if (res?.courses) setAvailableCourses(res.courses);
      })
      .catch((err) => {
        console.error('Failed to load courses meta', err);
      })
      .finally(() => {
        if (active) setLoadingMeta(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const totalErrors = errorCount(api.errors);

  const handleProceedToReview = () => {
    api.setSubmitAttempted(true);
    if (totalErrors > 0) {
      toast.error('נא למלא את כל שדות החובה בטופס לפני המעבר לסיכום.');
      const firstErrorKey = Object.keys(api.errors)[0];
      if (firstErrorKey) {
        const el = document.getElementById(firstErrorKey);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setMode('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!turnstileToken) {
      toast.error('חובה לסמן את אימות האבטחה ("אני לא רובוט") לפני שליחת הטופס.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await submitTeachingAssignment({
        data: {
          academicUnit: api.values.academicUnit,
          academicYear: api.values.academicYear,
          fullName: api.values.fullName,
          idNumber: api.values.idNumber,
          email: api.values.email,
          degreeName: api.values.degreeName,

          workedLastYear: api.values.workedLastYear as 'yes' | 'no',
          workedLastYearSemester: api.values.workedLastYearSemester || undefined,

          semester: api.values.semester,
          courseName: api.values.courseName,
          courseNumber: api.values.courseNumber,
          lecturerName: api.values.lecturerName,
          position: api.values.position,
          labSupervisorName: api.values.labSupervisorName,
          practiceGroupsNum: api.values.practiceGroupsNum,
          projectNumber: api.values.projectNumber,

          hasAdditionalPosition: api.values.hasAdditionalPosition as 'yes' | 'no',
          additionalPositionSemester: api.values.additionalPositionSemester || undefined,
          additionalFacultyName: api.values.additionalFacultyName || undefined,
          additionalPositionType: api.values.additionalPositionType || undefined,
          additionalPositionPercentage: api.values.additionalPositionPercentage || undefined,

          notes: api.values.notes || undefined,
          turnstileToken: turnstileToken || undefined,
        },
      });

      if (!res?.success || !res?.referenceCode) {
        throw new Error('שליחת הטופס לא הושלמה. אנא נסו שוב.');
      }

      const newItemId = res.itemId || '';

      // Upload lecturer approval file if attached
      if (api.approvalFile && newItemId) {
        try {
          const form = new FormData();
          form.append('file', api.approvalFile);
          form.append('itemId', newItemId);
          await uploadTeachingApprovalFile({ data: form });
        } catch (fileErr) {
          console.error('[TEACHING] Failed to upload approval file attachment', fileErr);
          toast.warning('הבקשה נרשמה בלוח, אך העלאת קובץ האישור דורשת בדיקה.');
        }
      }

      setReferenceCode(res.referenceCode);
      setCreatedItemId(newItemId);
      api.clearDraftStorage();
      setMode('submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('בקשת השיבוץ התקבלה בהצלחה!', {
        description: `אסמכתא: ${res.referenceCode}`,
      });
    } catch (err: unknown) {
      console.error('Submission failed', err);
      const msg = err instanceof Error ? err.message : 'אירעה שגיאה בשליחת הבקשה. אנא נסו שוב.';
      setSubmitError(msg);
      toast.error('שליחת הטופס נכשלה', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartNew = () => {
    api.resetForm();
    setReferenceCode('');
    setCreatedItemId('');
    setTurnstileToken(null);
    setMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (mode === 'submitted') {
    return (
      <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 lg:py-12">
        <TeachingReceipt
          values={api.values}
          referenceCode={referenceCode}
          itemId={createdItemId}
          onNewSubmission={handleStartNew}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-8 sm:px-6 lg:py-12">
      {/* Navigation Breadcrumb */}
      <nav aria-label="ניווט" className="no-print mb-6 flex items-center justify-between text-xs text-muted-foreground">
        <Link to="/" className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground">
          <ArrowRight className="size-3.5" aria-hidden="true" />
          חזרה לקטלוג הטפסים בפקולטה
        </Link>
        <div className="flex items-center gap-2">
          <span className={mode === 'edit' ? 'font-bold text-foreground' : ''}>1. מילוי הטופס</span>
          <ChevronRight className="size-3 -scale-x-100 text-muted-foreground/60" />
          <span className={mode === 'review' ? 'font-bold text-foreground' : ''}>2. בדיקה ואישור</span>
          <ChevronRight className="size-3 -scale-x-100 text-muted-foreground/60" />
          <span>3. אסמכתא</span>
        </div>
      </nav>

      {/* Dynamic Header & Guidelines */}
      <TeachingHero values={api.values} />

      {mode === 'edit' ? (
        <>
          {/* Quick Section Jump Menu */}
          <TeachingSectionNav />

          <form onSubmit={(e) => e.preventDefault()} noValidate className="space-y-8">
            <PersonalInfoSection
              values={api.values}
              setField={api.setField}
              markTouched={api.markTouched}
              fieldError={api.fieldError}
            />

            <EmploymentHistorySection
              values={api.values}
              setField={api.setField}
              markTouched={api.markTouched}
              fieldError={api.fieldError}
            />

            <CourseAssignmentSection
              values={api.values}
              setField={api.setField}
              approvalFile={api.approvalFile}
              setApprovalFile={api.setApprovalFile}
              markTouched={api.markTouched}
              fieldError={api.fieldError}
              availableCourses={availableCourses}
            />

            <AdditionalPositionSection
              values={api.values}
              setField={api.setField}
              markTouched={api.markTouched}
              fieldError={api.fieldError}
            />

            <NotesAndSecuritySection
              values={api.values}
              setField={api.setField}
              onTurnstileVerify={(token) => setTurnstileToken(token)}
              onTurnstileExpire={() => setTurnstileToken(null)}
            />

            {/* Action Bar */}
            <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))]/95 p-4 shadow-lg backdrop-blur-md">
              <div className="text-xs text-muted-foreground">
                {api.draftSavedAt ? (
                  <span>הטיוטה נשמרה אוטומטית בדפדפן בשעה {api.draftSavedAt}</span>
                ) : (
                  <span>כל השדות המסומנים ב-* הינם שדות חובה</span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={api.resetForm}
                  className="inline-flex h-11 items-center gap-2 rounded-lg border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-4 text-xs font-semibold text-foreground transition-colors hover:bg-[hsl(var(--surface-3))]"
                >
                  <RotateCcw className="size-4" />
                  איפוס טופס
                </button>

                <button
                  type="button"
                  onClick={handleProceedToReview}
                  className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary px-6 text-xs font-semibold text-primary-foreground shadow-xs transition-opacity hover:opacity-90"
                >
                  <Eye className="size-4" />
                  מעבר לבדיקת הפרטים ואישור
                </button>
              </div>
            </div>
          </form>
        </>
      ) : (
        <div className="mx-auto max-w-[900px] space-y-6">
          <div className="rounded-xl border border-[hsl(var(--rule))] bg-[hsl(var(--tint-sky))] p-4 text-xs sm:text-sm text-foreground">
            זהו סיכום פרטי בקשת השיבוץ לפני השליחה הסופית. אנא ודאו את נכונות כל הפרטים. לאחר הלחיצה על "שליחת הבקשה", תתווסף שורה חדשה למערכת השיבוצים באופן אוטומטי.
          </div>

          <div className="rounded-xl border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] shadow-xs">
            <TeachingReviewDocument values={api.values} approvalFile={api.approvalFile} />
          </div>

          {submitError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-xs font-medium text-destructive">
              <AlertCircle className="size-4 shrink-0 mt-0.5" />
              <span>{submitError}</span>
            </div>
          )}

          <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))]/95 p-4 shadow-lg backdrop-blur-md">
            <button
              type="button"
              onClick={() => setMode('edit')}
              disabled={submitting}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-[hsl(var(--rule))] bg-[hsl(var(--surface-2))] px-5 text-xs font-semibold text-foreground hover:bg-[hsl(var(--surface-3))] disabled:opacity-50"
            >
              <ArrowRight className="size-4" />
              חזרה לעריכת פרטים
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-emerald-600 dark:bg-emerald-500 px-7 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <RefreshCw className="size-4 animate-spin" />
                  שולח בקשה ללוח...
                </>
              ) : (
                <>
                  <Send className="size-4 -scale-x-100" />
                  שליחת הבקשה וקליטה בלוח
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
